import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router';

import dayjs from 'dayjs';

import { UserContext } from 'Context/user';

import { useUrlSlug } from 'Utilities/hooks/useUrlSlug';

import { getPostSaleStatus } from 'Services/privateApis';

import { authorizationHeader } from '../services/apiClient';
import { PostSaleStatusResponse } from '../types/apis.types';
import { FORBIDDEN_STATUS_CODES_FOR_VEHICLE_COLLECTION_PAGE, isAllowedPageRouteForHooks } from '../utils/routeChecker';
import { COLLECTION_DEADLINE_CONFIG } from '../VehicleCollectionSubjects';

export type UseGetCollectionDetails = {
  data: PostSaleStatusResponse | null;
  error?: Error | null;
  loading?: boolean;
  refetch?: () => void;
  refetchCount?: number;
  statusCode?: { current: number };
};

export const useGetCollectionDetails = (
  enquiryId: number | undefined,
) => {
  const { userState: user } = useContext(UserContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<PostSaleStatusResponse | null>(null);
  const [refetch, setRefetch] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const cancelled = useRef(false);
  const statusCode = useRef<number>(-1);

  const route = useRouteMatch();
  const slug = useUrlSlug();
  const abortController = useMemo(() => new AbortController(), []);
  const isAllowedPageRoute = isAllowedPageRouteForHooks({ hookName: 'useGetCollectionDetails', slug });

  const authToken = user?.auth_token;

  useEffect(() => () => {
    cancelled.current = true;
    abortController.abort();
  }, [abortController]);

  const setStatusCode = (status?: number) => {
    if (status) {
      statusCode.current = status;
    }
  };

  const autoRefetchConfig = useMemo(() => ({
    countdownInterval: 1000, // in milliseconds
    endOfCollectionHour: COLLECTION_DEADLINE_CONFIG.DEADLINE_HOUR,
    endOfCollectionMinute: COLLECTION_DEADLINE_CONFIG.DEADLINE_MINUTE,
    endOfCollectionSecond: COLLECTION_DEADLINE_CONFIG.DEADLINE_SECOND,
    timeRangeForRefetch: 1, // in seconds
  }), []);

  const getCollectionDetails = useCallback(async () => {
    setLoading(true);

    try {
      if (!enquiryId) {
        throw new Error('Missing enquiry Id for get collection details request');
      }

      if (!authToken) {
        throw new Error('Missing auth token for mock get collection details request');
      }

      const response = await getPostSaleStatus(
        enquiryId.toString(),
        authorizationHeader(authToken),
        setStatusCode,
        abortController,
      );

      const { signal } = abortController;
      if (signal.aborted) {
        throw new Error('get collection details request aborted');
      }

      if (!cancelled?.current) {
        setData(response);
      }
    } catch (err) {
      if (cancelled?.current) {
        return;
      }
      setError(err as Error);
      if (statusCode?.current !== FORBIDDEN_STATUS_CODES_FOR_VEHICLE_COLLECTION_PAGE) {
        window?.Sentry?.captureException?.(err, {
          extra: { cancelled, enquiryId, url: route?.url },
        });
      }
    } finally {
      if (!cancelled?.current) {
        setLoading(false);
      }
    }
  }, [enquiryId, authToken, abortController, route?.url]);

  useEffect(() => {
    if (isAllowedPageRoute) {
      getCollectionDetails();
    }
  }, [refetch, getCollectionDetails, isAllowedPageRoute]);

  // eslint-disable-next-line consistent-return
  const scheduledDateCountdown = useCallback(() => {
    if (data?.collection?.scheduledCollectionDate) {
      const interval = setInterval(() => {
        const currentTime = dayjs();
        const endOfScheduledCollectionTime = dayjs(
          data?.collection?.scheduledCollectionDate,
        ).hour(
          autoRefetchConfig.endOfCollectionHour,
        ).minute(
          autoRefetchConfig.endOfCollectionMinute,
        ).second(
          autoRefetchConfig.endOfCollectionSecond,
        );
        const endOfScheduledCollectionTimePlusOneSecond = endOfScheduledCollectionTime.add(autoRefetchConfig.timeRangeForRefetch, 'second');
        const timeToRefetchStatus = currentTime.isSameOrAfter(endOfScheduledCollectionTime)
        && currentTime.isBefore(endOfScheduledCollectionTimePlusOneSecond);

        if (timeToRefetchStatus) {
          clearInterval(interval);
          setRefetch(refetch + 1);
        }
      }, autoRefetchConfig.countdownInterval);

      return interval;
    }
  }, [data?.collection?.scheduledCollectionDate, refetch, autoRefetchConfig]);

  useEffect(() => {
    const timer = scheduledDateCountdown();
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [data, scheduledDateCountdown]);

  return {
    data,
    error,
    loading,
    refetch: () => setRefetch(refetch + 1),
    refetchCount: refetch,
    statusCode,
  };
};
